import { roundToDecimals, sortArray } from './utilities'

export type PrecisionAmount = {
	value: number
	precision: number
}
export type TaxAmount = {
	gross: PrecisionAmount
	net: PrecisionAmount
	tax: PrecisionAmount
}
export const getCurrencyDecimals = (currency: string) => {
	const formatter = new Intl.NumberFormat([], {
		style: 'currency',
		currency
	})
	const decimals = formatter.resolvedOptions().minimumFractionDigits
	if (decimals === undefined) {
		throw new Error(`Unable to determine number of decimals for currency: ${currency}`)
	}
	return decimals
}

// JsonValue is used to allow for the use of Prisma's Json type
export type AmountInput = number | PrecisionAmount

export const isPrecisionAmount = (value: any): value is PrecisionAmount => {
	return (
		typeof value === 'object' &&
		!!value &&
		'value' in value &&
		typeof value.value === 'number' &&
		!isNaN(value.value) &&
		'precision' in value &&
		typeof value.precision === 'number' &&
		!isNaN(value.precision)
	)
}

export const isTaxAmount = (value: any): value is TaxAmount => {
	return typeof value === 'object' && !!value && 'gross' in value && 'net' in value && 'tax' in value
}

export const taxAmount = () => ({
	gross: precisionAmount(0),
	net: precisionAmount(0),
	tax: precisionAmount(0)
})

export const precisionAmount = (value: number, precisionToConvertTo = 2): PrecisionAmount => {
	if (typeof value === 'number') {
		return convertToPrecision(
			{
				value,
				precision: 0
			},
			precisionToConvertTo
		)
	} else {
		throw new Error(`Invalid amount input: ${JSON.stringify(value)}`)
	}
}

export const createPrecisionAmountIfNeeded = (value: AmountInput, precisionToConvertTo = 2): PrecisionAmount => {
	if (typeof value === 'number') {
		return precisionAmount(value, precisionToConvertTo)
	} else {
		return value
	}
}
export const convertToPrecision = (value: PrecisionAmount, precision = 2): PrecisionAmount => {
	if (value.precision === precision) {
		return value
	}

	return {
		value: Math.round(value.value * Math.pow(10, precision - value.precision)),
		precision
	}
}
export const convertTaxToPrecision = (value: TaxAmount, precision = 2): TaxAmount => {
	const gross = convertToPrecision(value.gross, precision)
	const tax = convertToPrecision(value.tax, precision)
	const net = subtract(gross, tax)
	return {
		gross,
		net,
		tax
	}
}
export const fromPrecision = (input: AmountInput) => {
	if (typeof input === 'number') {
		return input
	}
	return input.value / Math.pow(10, input.precision)
}

export const toMajorUnits = (input: AmountInput) => {
	return convertToPrecision(createPrecisionAmountIfNeeded(input), 0).value
}

export const toMinorUnits = (input: AmountInput) => {
	return convertToPrecision(createPrecisionAmountIfNeeded(input), 2).value
}

export const add = <T extends AmountInput | TaxAmount, TResult = T extends TaxAmount ? TaxAmount : PrecisionAmount>(
	a: T | AmountInput,
	b: T | AmountInput
): TResult => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			return {
				gross: add(a.gross, b.gross),
				net: add(a.net, b.net),
				tax: add(a.tax, b.tax)
			} as TResult
		}
		throw new Error(`Cannot add tax amount to non-tax amount: ${JSON.stringify(a)} + ${JSON.stringify(b)}`)
	}

	const aPrecisionAmount = createPrecisionAmountIfNeeded(a)
	const bPrecisionAmount = createPrecisionAmountIfNeeded(b)

	const precision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)
	const aConverted = convertToPrecision(aPrecisionAmount, precision)
	const bConverted = convertToPrecision(bPrecisionAmount, precision)

	return {
		value: Math.round(aConverted.value + bConverted.value),
		precision
	} as TResult
}

export const subtract = <
	T extends AmountInput | TaxAmount,
	TResult = T extends TaxAmount ? TaxAmount : PrecisionAmount
>(
	a: T | AmountInput,
	b: T | AmountInput
): TResult => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			return {
				gross: subtract(a.gross, b.gross),
				net: subtract(a.net, b.net),
				tax: subtract(a.tax, b.tax)
			} as TResult
		}
		throw new Error(`Cannot add tax amount to non-tax amount: ${JSON.stringify(a)} + ${JSON.stringify(b)}`)
	}

	const aPrecisionAmount = createPrecisionAmountIfNeeded(a)
	const bPrecisionAmount = createPrecisionAmountIfNeeded(b)

	const precision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)
	const aConverted = convertToPrecision(aPrecisionAmount, precision)
	const bConverted = convertToPrecision(bPrecisionAmount, precision)

	return {
		value: Math.round(aConverted.value - bConverted.value),
		precision
	} as TResult
}

export const multiply = <
	T extends AmountInput | TaxAmount,
	TResult = T extends TaxAmount ? TaxAmount : PrecisionAmount
>(
	a: T,
	b: AmountInput
): TResult => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			throw new Error(`Cannot divide tax amount by other tax amount: ${JSON.stringify(a)} / ${JSON.stringify(b)}`)
		}
		const taxAmount = (isTaxAmount(a) ? a : b) as TaxAmount
		const nonTaxAmount = (isTaxAmount(a) ? b : a) as AmountInput
		const gross = multiply(taxAmount.gross, nonTaxAmount)
		const tax = multiply(taxAmount.tax, nonTaxAmount)
		const net = subtract(gross, tax)
		return {
			gross,
			net,
			tax
		} as TResult
	}

	let aPrecisionAmount = createPrecisionAmountIfNeeded(a)
	let bPrecisionAmount = createPrecisionAmountIfNeeded(b)

	const outputPrecision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)

	aPrecisionAmount = convertToPrecision(aPrecisionAmount, outputPrecision)
	bPrecisionAmount = convertToPrecision(bPrecisionAmount, outputPrecision)

	return convertToPrecision(
		{
			value: Math.round(aPrecisionAmount.value * bPrecisionAmount.value),
			precision: aPrecisionAmount.precision + bPrecisionAmount.precision
		},
		outputPrecision
	) as TResult
}

export const divide = <T extends AmountInput | TaxAmount, TResult = T extends TaxAmount ? TaxAmount : PrecisionAmount>(
	a: T,
	b: AmountInput
): TResult => {
	if (isTaxAmount(a) && isTaxAmount(b)) {
		throw new Error(`Cannot divide tax amount by other tax amount: ${JSON.stringify(a)} / ${JSON.stringify(b)}`)
	}
	if (isTaxAmount(a)) {
		const taxAmount = a as TaxAmount
		const nonTaxAmount = b as AmountInput
		const gross = divide(taxAmount.gross, nonTaxAmount)
		const net = divide(taxAmount.net, nonTaxAmount)
		const tax = subtract(gross, net)
		return {
			gross,
			net,
			tax
		} as TResult
	}

	let aPrecisionAmount = createPrecisionAmountIfNeeded(a)
	const bPrecisionAmount = createPrecisionAmountIfNeeded(b)

	if (bPrecisionAmount.value === 0) {
		throw new Error('Division by zero in currency.divide')
	}

	const outputPrecision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)

	aPrecisionAmount = convertToPrecision(aPrecisionAmount, aPrecisionAmount.precision + bPrecisionAmount.precision)
	return convertToPrecision(
		{
			value: Math.round(aPrecisionAmount.value / bPrecisionAmount.value),
			precision: aPrecisionAmount.precision - bPrecisionAmount.precision
		},
		outputPrecision
	) as TResult
}
export const sum = (values: AmountInput[]) => {
	return values.reduce<PrecisionAmount>((acc, next) => add<AmountInput>(acc, next), precisionAmount(0))
}

export const addTax = (a: TaxAmount, b: TaxAmount): TaxAmount => {
	if (!isTaxAmount(a) || !isTaxAmount(b)) {
		throw new Error(`Cannot add tax amount to non-tax amount: ${JSON.stringify(a)} + ${JSON.stringify(b)}`)
	}
	return {
		gross: add(a.gross, b.gross),
		net: add(a.net, b.net),
		tax: add(a.tax, b.tax)
	}
}

export const subtractTax = (a: TaxAmount, b: TaxAmount): TaxAmount => {
	if (!isTaxAmount(a) || !isTaxAmount(b)) {
		throw new Error(`Cannot subtract tax amount from non-tax amount: ${JSON.stringify(a)} - ${JSON.stringify(b)}`)
	}
	return {
		gross: subtract(a.gross, b.gross),
		net: subtract(a.net, b.net),
		tax: subtract(a.tax, b.tax)
	}
}

export const multiplyTax = (a: TaxAmount, b: AmountInput): TaxAmount => {
	const gross = multiply(a.gross, b)
	const tax = multiply(a.tax, b)
	const net = subtract(gross, tax)
	return {
		gross,
		net,
		tax
	}
}

export const divideTax = (a: TaxAmount, b: AmountInput): TaxAmount => {
	const taxAmount = a as TaxAmount
	const gross = divide(taxAmount.gross, b)
	const net = divide(taxAmount.net, b)
	const tax = subtract(gross, net)
	return {
		gross,
		net,
		tax
	}
}

export const sumTax = (values: TaxAmount[]) => {
	return values.reduce<TaxAmount>((acc, next) => add(acc, next), taxAmount())
}

export const compare = (a: AmountInput, b: AmountInput) => {
	let aPrecisionAmount = createPrecisionAmountIfNeeded(a)
	let bPrecisionAmount = createPrecisionAmountIfNeeded(b)
	const outputPrecision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)

	aPrecisionAmount = convertToPrecision(aPrecisionAmount, outputPrecision)
	bPrecisionAmount = convertToPrecision(bPrecisionAmount, outputPrecision)

	return aPrecisionAmount.value - bPrecisionAmount.value
}

export const largerThan = (a: AmountInput, b: AmountInput) => {
	return compare(a, b) > 0
}

export const isEqualTo = (a: AmountInput, b: AmountInput) => {
	return compare(a, b) === 0
}

export const smallerThan = (a: AmountInput, b: AmountInput) => {
	return compare(a, b) < 0
}

export const clamp = (value: PrecisionAmount, options: { min?: AmountInput; max?: AmountInput }) => {
	if (options.min !== undefined && smallerThan(value, options.min)) {
		return options.min
	}
	if (options.max !== undefined && largerThan(value, options.max)) {
		return options.max
	}
	return value
}

export const computeTaxAmountWithRounding = (
	gross: PrecisionAmount,
	currencyCode: string,
	taxInclusive: boolean,
	taxRateValue: PrecisionAmount
): TaxAmount => {
	// We make sure the total price is rounded to the correct decimal count for the currency, also for ISK
	const totalPrice = roundCurrencyToNearestDecimals(gross, currencyCode)

	// We can then compute the tax amount from the total price and always using 2 decimals
	const totalPriceTaxAmount = computeTax(totalPrice, taxInclusive, taxRateValue, 2)

	// Finally we round the tax amount to the correct decimal count for the currency. The total price is already rounded to the nearest whole number.
	return roundCurrencyToNearestDecimals(totalPriceTaxAmount, currencyCode, {
		ISK: 2
	})
}

export const roundCurrencyToNearestDecimals = <
	T extends AmountInput | TaxAmount,
	TResult = T extends TaxAmount ? TaxAmount : PrecisionAmount
>(
	amount: T,
	currency: string,
	overrides: Record<string, number> = {}
): TResult => {
	if (isTaxAmount(amount)) {
		return {
			gross: roundCurrencyToNearestDecimals(amount.gross, currency, overrides),
			net: roundCurrencyToNearestDecimals(amount.net, currency, overrides),
			tax: roundCurrencyToNearestDecimals(amount.tax, currency, overrides)
		} as TResult
	}

	let decimals = 0
	if (overrides[currency]) {
		decimals = overrides[currency]
	} else {
		decimals = getCurrencyDecimals(currency)
	}
	const convertedAmount = createPrecisionAmountIfNeeded(amount)
	return convertToPrecision(
		{
			value: Math.round(fromPrecision(convertedAmount) * Math.pow(10, decimals)),
			precision: decimals
		},
		decimals
	) as TResult
}

export const computeTax = (
	price: PrecisionAmount,
	taxInclusive: boolean,
	taxRate: PrecisionAmount,
	precisionToConvertTo = 2
): TaxAmount => {
	let gross: PrecisionAmount
	let tax: PrecisionAmount
	let net: PrecisionAmount
	if (taxInclusive) {
		gross = convertToPrecision(price, precisionToConvertTo)
		net = divide(gross, add(1, taxRate))
		tax = subtract(gross, net)
	} else {
		net = convertToPrecision(price, precisionToConvertTo)
		tax = multiply(
			convertToPrecision(price, precisionToConvertTo),
			convertToPrecision(taxRate, precisionToConvertTo)
		)
		gross = add(net, tax)
	}
	return { gross, tax, net }
}

export const distributeRemainder = (total: number, items: { id: string; percentage: number }[]) => {
	if (total < 0) {
		throw new Error(`Total must be non-negative: ${total}`)
	}

	const totalPercentage = roundToDecimals(
		items.reduce((acc, item) => acc + item.percentage, 0),
		2
	)
	if (totalPercentage !== 1) {
		throw new Error(`Total percentage must be 1: ${totalPercentage}`)
	}

	const distributed = items.map(item => {
		const amount = total * item.percentage
		const roundingDifference = amount - Math.floor(amount)
		return {
			id: item.id,
			amount,
			floorAmount: Math.floor(amount),
			roundingDifference
		}
	})
	sortArray(distributed, x => x.roundingDifference, 'desc')

	let remainder = total - distributed.reduce((acc, x) => acc + x.floorAmount, 0)
	// Remainder is always positive since we use floor
	for (const item of distributed) {
		if (remainder > 0) {
			item.amount = item.floorAmount + 1
			remainder--
		} else {
			item.amount = item.floorAmount
		}
	}

	const result: Record<string, number> = {}
	for (const item of distributed) {
		result[item.id] = item.amount
	}
	return result
}
